import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReceiptText, Loader2, Download, Search } from "lucide-react";
import { api, fmtDate } from "@/lib/apiClient";
import { rupiah } from "@/components/InvoiceDialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);
const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus",
  "September", "Oktober", "November", "Desember"];

/** Alokasikan pembayaran JO ke tiap item secara berurutan untuk kolom Kredit per item. */
const withKredit = (r) => {
  const p = r.payment || {};
  let sisaBayar = num(p.tunai) + num(p.dp) + num(p.pelunasan);
  return (r.lines || []).map((l) => {
    const nilai = num(l.nilai);
    const pakai = Math.min(sisaBayar, nilai);
    sisaBayar -= pakai;
    return { ...l, kredit: Math.max(nilai - pakai, 0) };
  });
};

export const SalesInvoiceBoard = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState("");
  const [tgl, setTgl] = useState("");
  const [bulan, setBulan] = useState("ALL");
  const [tahun, setTahun] = useState("ALL");

  const load = useCallback(async () => {
    const { data } = await api.get("/sales-invoices");
    setRows(data.map((r) => ({ ...r, lines: withKredit(r) })));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const years = useMemo(
    () => [...new Set(rows.map((r) => (r.tanggal || "").slice(0, 4)).filter(Boolean))].sort(),
    [rows]
  );

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        const t = r.tanggal || "";
        if (client && !r.client_name.toLowerCase().includes(client.toLowerCase())) return false;
        if (tgl && t !== tgl) return false;
        if (bulan !== "ALL" && t.slice(5, 7) !== bulan) return false;
        if (tahun !== "ALL" && t.slice(0, 4) !== tahun) return false;
        return true;
      }),
    [rows, client, tgl, bulan, tahun]
  );

  const perKategori = useMemo(() => {
    const map = {};
    filtered.forEach((r) =>
      r.lines.forEach((l) => {
        const k = l.kategori || "LAINNYA";
        map[k] = (map[k] || 0) + num(l.nilai);
      })
    );
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const nilaiTotal = perKategori.reduce((s, [, v]) => s + v, 0);
  const piutang = filtered.reduce(
    (s, r) => s + r.lines.reduce((a, l) => a + num(l.kredit), 0),
    0
  );

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/sales-invoices/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FAKTUR-JUAL-PAS_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel faktur jual berhasil diunduh");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyiapkan Excel");
    } finally {
      setBusy(false);
    }
  };

  let seq = 0;

  return (
    <div className="space-y-6" data-testid="sales-invoice-board">
      <div className="glass rounded-2xl p-6 md:p-8 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <ReceiptText className="w-5 h-5 text-blue-400" />
            <div>
              <h3 className="font-display text-xl font-semibold">Papan Faktur Jual</h3>
              <p className="text-xs text-slate-400 mt-1">
                Semua data tersistem dari Papan Admin &amp; Papan Pembayaran ·{" "}
                <span className="text-blue-300 font-semibold" data-testid="sales-total-jo">
                  {filtered.length} JO
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Piutang</div>
              <div className="font-display text-xl font-light text-amber-300" data-testid="sales-piutang-total">
                {rupiah(piutang)}
              </div>
            </div>
            <button
              type="button"
              data-testid="sales-export-btn"
              onClick={download}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors duration-200"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {busy ? "Menyiapkan Excel..." : "Download Excel"}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="sales-filter-client"
              placeholder="Cari nama konsumen"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              className="h-10 pl-9 w-56 bg-slate-900/70 border-white/10"
            />
          </div>
          <Input
            data-testid="sales-filter-tanggal"
            type="date"
            value={tgl}
            onChange={(e) => setTgl(e.target.value)}
            className="h-10 w-auto bg-slate-900/70 border-white/10"
          />
          <select
            data-testid="sales-filter-bulan"
            value={bulan}
            onChange={(e) => setBulan(e.target.value)}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Semua Bulan</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
            ))}
          </select>
          <select
            data-testid="sales-filter-tahun"
            value={tahun}
            onChange={(e) => setTahun(e.target.value)}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Semua Tahun</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {(client || tgl || bulan !== "ALL" || tahun !== "ALL") && (
            <button
              type="button"
              data-testid="sales-filter-reset"
              onClick={() => { setClient(""); setTgl(""); setBulan("ALL"); setTahun("ALL"); }}
              className="text-xs text-slate-400 hover:text-blue-300 transition-colors duration-200"
            >
              reset filter
            </button>
          )}
        </div>

        <div data-testid="sales-recap-kategori">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Rekap Nilai Faktur per Kategori
            </span>
            <span className="text-[11px] text-slate-400">
              Total: <b className="text-emerald-300" data-testid="sales-grand-total">{rupiah(nilaiTotal)}</b>
            </span>
          </div>
          {perKategori.length === 0 ? (
            <p className="mt-3 text-xs text-slate-500">Belum ada nilai faktur pada filter ini.</p>
          ) : (
            <div className="mt-3 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
              {perKategori.map(([k, v]) => (
                <div
                  key={k}
                  data-testid={`sales-recap-${k}`}
                  className="rounded-xl border border-white/10 bg-slate-900/60 p-3"
                >
                  <span className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-blue-200">
                    {k}
                  </span>
                  <div className="mt-2 font-display text-base text-slate-100">{rupiah(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="sales-empty">
          Tidak ada data pada filter ini.
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1800px] text-[11px]" data-testid="sales-invoice-table">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-[0.14em] text-slate-400">
                  {["No", "No. JO", "No Nota", "No Invoice", "Nama Client", "Deskripsi", "Jenis Order", "Kategori",
                    "Uk (P)", "Uk (L)", "Qty", "Jumlah", "Price", "Pembulatan", "Diskon", "Nilai",
                    "Tunai (Kas/Bank)", "Kredit", "DP (Kas/Bank)", "Pelunasan (Kas/Bank)",
                    "Tgl Pembayaran", "Tgl Pelunasan"].map((h) => (
                    <th key={h} className="px-2 py-3 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const p = r.payment || {};
                  const lines = r.lines.length > 0 ? r.lines : [{}];
                  return lines.map((l, li) => {
                    seq += 1;
                    const first = li === 0;
                    return (
                      <tr
                        key={`${r.jo_id}-${li}`}
                        className="border-t border-white/5 align-top"
                        data-testid={`sales-row-${r.jo_number}-${li}`}
                      >
                        <td className="px-2 py-2 text-slate-400">{seq}</td>
                        <td className="px-2 py-2 text-blue-200 font-semibold whitespace-nowrap">
                          {r.jo_number}
                          <div className="text-[9px] text-slate-500">{fmtDate(r.tanggal)}</div>
                        </td>
                        <td className="px-2 py-2 text-slate-300 whitespace-nowrap"
                            data-testid={`sales-nota-${r.jo_number}`}>
                          {r.nota_no || `NOTA-${r.jo_number}`}
                        </td>
                        <td className="px-2 py-2 text-emerald-200 whitespace-nowrap"
                            data-testid={`sales-invoiceno-${r.jo_number}`}>
                          {r.invoice_no || "-"}
                        </td>
                        <td className="px-2 py-2 text-slate-200">{r.client_name}</td>
                        <td className="px-2 py-2 text-slate-300 min-w-[150px]">{l.deskripsi || "-"}</td>
                        <td className="px-2 py-2 text-slate-300">{l.jenis_order || "-"}</td>
                        <td className="px-2 py-2 text-blue-200">{l.kategori || "-"}</td>
                        <td className="px-2 py-2 text-slate-300">{l.panjang || "-"}</td>
                        <td className="px-2 py-2 text-slate-300">{l.lebar || "-"}</td>
                        <td className="px-2 py-2 text-slate-300">{l.qty || "-"}</td>
                        <td className="px-2 py-2 text-slate-100">{num(l.jumlah).toLocaleString("id-ID")}</td>
                        <td className="px-2 py-2 text-slate-300 whitespace-nowrap">{rupiah(l.price)}</td>
                        <td className="px-2 py-2 text-slate-300 whitespace-nowrap">{rupiah(l.pembulatan)}</td>
                        <td className="px-2 py-2 text-red-300 whitespace-nowrap">{rupiah(l.diskon)}</td>
                        <td className="px-2 py-2 text-emerald-300 font-semibold whitespace-nowrap"
                            data-testid={`sales-nilai-${r.jo_number}-${li}`}>
                          {rupiah(l.nilai)}
                        </td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">
                          {first && num(p.tunai) > 0 ? `${rupiah(p.tunai)} · ${p.tunai_akun || "-"}` : "-"}
                        </td>
                        <td className="px-2 py-2 text-amber-300 font-semibold whitespace-nowrap"
                            data-testid={`sales-kredit-${r.jo_number}-${li}`}>
                          {rupiah(l.kredit)}
                        </td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">
                          {first && num(p.dp) > 0 ? `${rupiah(p.dp)} · ${p.dp_akun || "-"}` : "-"}
                        </td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">
                          {first && num(p.pelunasan) > 0
                            ? `${rupiah(p.pelunasan)} · ${p.pelunasan_akun || "-"}`
                            : "-"}
                        </td>
                        <td className="px-2 py-2 text-slate-400 whitespace-nowrap">
                          {first ? p.tanggal_bayar || "-" : "-"}
                        </td>
                        <td className="px-2 py-2 text-slate-400 whitespace-nowrap">
                          {first ? p.tanggal_pelunasan || "-" : "-"}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
